import { DialogCloseEvent } from '@theme/dialog';
import { clamp, preventDefault, isMobileBreakpoint } from '@theme/utilities';
import { Component } from '@theme/component';

/* =========================================================
   RAW AURA — MOBILE PRODUCT IMAGE ZOOM
   Initial state = 1x / full image
========================================================= */

const MIN_ZOOM = 1;
const MAX_ZOOM = 5;
const DOUBLE_TAP_ZOOM = 1.5;

const DOUBLE_TAP_DELAY = 300;
const DOUBLE_TAP_DISTANCE = 50;
const DRAG_THRESHOLD = 10;

/**
 * @typedef {object} Refs
 * @property {HTMLImageElement} image
 */

/**
 * @typedef {object} Point
 * @property {number} x
 * @property {number} y
 */

/** @extends {Component<Refs>} */
export class DragZoomWrapper extends Component {
  requiredRefs = ['image'];

  #controller = new AbortController();

  /** @type {number} */
  #scale = MIN_ZOOM;

  /** @type {number} */
  #startScale = MIN_ZOOM;

  /** @type {number} */
  #initialDistance = 0;

  /** @type {Point} */
  #translate = {
    x: 0,
    y: 0,
  };

  /** @type {Point} */
  #startTranslate = {
    x: 0,
    y: 0,
  };

  /** @type {Point} */
  #startPosition = {
    x: 0,
    y: 0,
  };

  /** @type {boolean} */
  #isDragging = false;

  /** @type {boolean} */
  #hasDraggedBeyondThreshold = false;

  /** @type {boolean} */
  #initialized = false;

  /** @type {boolean} */
  #hasManualZoom = false;

  /** @type {number} */
  #lastTapTime = 0;

  /** @type {Point | null} */
  #lastTapPosition = null;

  /** @type {number | null} */
  #animationFrame = null;


  /* =========================================================
     IMAGE REF
  ========================================================= */

  get #image() {
    return this.refs.image;
  }


  /* =========================================================
     CONNECT
  ========================================================= */

  connectedCallback() {
    super.connectedCallback();

    if (!this.#image) return;

    this.#resizeObserver.observe(this);

    window.addEventListener(
      DialogCloseEvent.eventName,
      this.#resetZoom
    );

    /*
      Desktop keeps Horizon's normal presentation.
      Touch interaction is initialized only on mobile.
    */
    if (!isMobileBreakpoint()) {
      this.#applyTransform();
      return;
    }

    this.#initializeTouchEvents();

    /*
      IMPORTANT:
      Mobile zoom must open at 1x so that the whole image
      is visible before the customer chooses to zoom.
    */
    this.#resetZoom();
  }


  /* =========================================================
     DISCONNECT
  ========================================================= */

  disconnectedCallback() {
    super.disconnectedCallback();

    window.removeEventListener(
      DialogCloseEvent.eventName,
      this.#resetZoom
    );

    this.#controller.abort();

    this.#resizeObserver.disconnect();

    this.#cancelAnimationFrame();
  }


  /* =========================================================
     INITIALIZE TOUCH EVENTS
  ========================================================= */

  #initializeTouchEvents() {
    if (this.#initialized) return;

    this.#initialized = true;

    const options = {
      passive: false,
      signal: this.#controller.signal,
    };

    this.addEventListener(
      'touchstart',
      this.#handleTouchStart,
      options
    );

    this.addEventListener(
      'touchmove',
      this.#handleTouchMove,
      options
    );

    this.addEventListener(
      'touchend',
      this.#handleTouchEnd,
      options
    );

    this.addEventListener(
      'touchcancel',
      this.#handleTouchEnd,
      options
    );

    this.#applyTransform();
  }


  /* =========================================================
     RESIZE
  ========================================================= */

  #handleResize = () => {
    if (
      !this.#initialized &&
      isMobileBreakpoint()
    ) {
      this.#initializeTouchEvents();
    }

    /*
      If browser width/orientation changes while zoomed,
      keep the image inside the current wrapper.
    */
    this.#requestTransformUpdate();
  };


  #resizeObserver = new ResizeObserver(
    this.#handleResize
  );


  /* =========================================================
     TOUCH START
  ========================================================= */

  #handleTouchStart = (event) => {
    preventDefault(event);

    const touchCount = event.touches.length;


    /* ---------------------------------------------------------
       PINCH START
    --------------------------------------------------------- */

    if (touchCount === 2) {
      const firstTouch = event.touches[0];
      const secondTouch = event.touches[1];

      if (!firstTouch || !secondTouch) return;

      this.#initialDistance = getDistance(
        firstTouch,
        secondTouch
      );

      this.#startScale = this.#scale;

      this.#isDragging = false;

      return;
    }


    /* ---------------------------------------------------------
       SINGLE TOUCH / DOUBLE TAP
    --------------------------------------------------------- */

    if (touchCount !== 1) return;

    const touch = event.touches[0];

    if (!touch) return;

    const currentTime = performance.now();

    const currentPoint = {
      x: touch.clientX,
      y: touch.clientY,
    };

    const elapsed =
      currentTime - this.#lastTapTime;


    /*
      Detect double tap.
    */
    if (
      this.#lastTapPosition &&
      elapsed < DOUBLE_TAP_DELAY
    ) {
      const distance = getDistance(
        currentPoint,
        this.#lastTapPosition
      );

      if (distance < DOUBLE_TAP_DISTANCE) {
        this.#handleDoubleTap(touch);

        this.#lastTapTime = 0;

        this.#lastTapPosition = null;

        this.#isDragging = false;

        return;
      }
    }


    /*
      Store current tap for possible double tap.
    */
    this.#lastTapTime = currentTime;

    this.#lastTapPosition = currentPoint;


    /*
      Begin drag gesture.
    */
    this.#startPosition = currentPoint;

    this.#startTranslate = {
      x: this.#translate.x,
      y: this.#translate.y,
    };

    this.#isDragging = true;

    this.#hasDraggedBeyondThreshold = false;
  };


  /* =========================================================
     TOUCH MOVE
  ========================================================= */

  #handleTouchMove = (event) => {
    preventDefault(event);

    const touchCount = event.touches.length;


    /* ---------------------------------------------------------
       PINCH
    --------------------------------------------------------- */

    if (touchCount === 2) {
      const firstTouch = event.touches[0];
      const secondTouch = event.touches[1];

      if (!firstTouch || !secondTouch) return;

      this.#processPinch(
        firstTouch,
        secondTouch
      );

      return;
    }


    /* ---------------------------------------------------------
       DRAG
    --------------------------------------------------------- */

    if (
      touchCount === 1 &&
      this.#isDragging
    ) {
      const touch = event.touches[0];

      if (!touch) return;

      this.#processDrag(touch);
    }
  };


  /* =========================================================
     TOUCH END
  ========================================================= */

  #handleTouchEnd = (event) => {
    if (event.touches.length === 0) {
      this.#isDragging = false;

      this.#hasDraggedBeyondThreshold = false;

      this.#requestTransformUpdate();
    }
  };


  /* =========================================================
     PINCH ZOOM
  ========================================================= */

  #processPinch(firstTouch, secondTouch) {
    if (!this.#initialDistance) return;


    const currentDistance = getDistance(
      firstTouch,
      secondTouch
    );


    /*
      Midpoint of the two fingers.
    */
    const midpointX =
      (firstTouch.clientX +
        secondTouch.clientX) /
      2;

    const midpointY =
      (firstTouch.clientY +
        secondTouch.clientY) /
      2;


    const previousScale = this.#scale;


    /*
      New pinch scale.
    */
    const calculatedScale =
      (currentDistance /
        this.#initialDistance) *
      this.#startScale;


    this.#scale = clamp(
      calculatedScale,
      MIN_ZOOM,
      MAX_ZOOM
    );


    this.#hasManualZoom = true;


    /*
      Keep pinch midpoint visually stable.
    */
    if (previousScale > 0) {
      const wrapperRect =
        this.getBoundingClientRect();

      const wrapperCenterX =
        wrapperRect.left +
        wrapperRect.width / 2;

      const wrapperCenterY =
        wrapperRect.top +
        wrapperRect.height / 2;


      const offsetX =
        midpointX - wrapperCenterX;

      const offsetY =
        midpointY - wrapperCenterY;


      const scaleDifference =
        this.#scale /
          previousScale -
        1;


      this.#translate.x -=
        (offsetX * scaleDifference) /
        this.#scale;

      this.#translate.y -=
        (offsetY * scaleDifference) /
        this.#scale;
    }


    /*
      Return to exact centered full-image state at 1x.
    */
    if (this.#scale <= MIN_ZOOM) {
      this.#scale = MIN_ZOOM;

      this.#translate = {
        x: 0,
        y: 0,
      };
    }


    this.#isDragging = false;

    this.#requestTransformUpdate();
  }


  /* =========================================================
     DRAG
  ========================================================= */

  #processDrag(touch) {
    /*
      At 1x the entire image should stay centered.
      There is no reason to drag it.
    */
    if (this.#scale <= MIN_ZOOM) {
      this.#translate = {
        x: 0,
        y: 0,
      };

      return;
    }


    const currentPoint = {
      x: touch.clientX,
      y: touch.clientY,
    };


    const movementDistance =
      getDistance(
        currentPoint,
        this.#startPosition
      );


    if (
      !this.#hasDraggedBeyondThreshold &&
      movementDistance < DRAG_THRESHOLD
    ) {
      return;
    }


    this.#hasDraggedBeyondThreshold = true;


    const deltaX =
      currentPoint.x -
      this.#startPosition.x;

    const deltaY =
      currentPoint.y -
      this.#startPosition.y;


    /*
      Translation is stored in pre-scale coordinates.
    */
    this.#translate.x =
      this.#startTranslate.x +
      deltaX / this.#scale;

    this.#translate.y =
      this.#startTranslate.y +
      deltaY / this.#scale;


    this.#requestTransformUpdate();
  }


  /* =========================================================
     DOUBLE TAP
  ========================================================= */

  #handleDoubleTap(touch) {
    /*
      If user manually pinch-zoomed, first double tap
      simply returns to the full image.
    */
    if (this.#hasManualZoom) {
      this.#scale = MIN_ZOOM;

      this.#translate = {
        x: 0,
        y: 0,
      };

      this.#hasManualZoom = false;

      this.#requestTransformUpdate();

      return;
    }


    /*
      1x -> 1.5x
      anything else -> 1x
    */
    if (
      Math.abs(
        this.#scale - MIN_ZOOM
      ) < 0.05
    ) {
      this.#zoomAtPoint(
        DOUBLE_TAP_ZOOM,
        touch.clientX,
        touch.clientY
      );
    } else {
      this.#scale = MIN_ZOOM;

      this.#translate = {
        x: 0,
        y: 0,
      };

      this.#requestTransformUpdate();
    }
  }


  /* =========================================================
     ZOOM AROUND TOUCH POINT
  ========================================================= */

  #zoomAtPoint(
    targetScale,
    clientX,
    clientY
  ) {
    const oldScale = this.#scale;


    this.#scale = clamp(
      targetScale,
      MIN_ZOOM,
      MAX_ZOOM
    );


    const wrapperRect =
      this.getBoundingClientRect();


    const centerX =
      wrapperRect.left +
      wrapperRect.width / 2;

    const centerY =
      wrapperRect.top +
      wrapperRect.height / 2;


    const offsetX =
      clientX - centerX;

    const offsetY =
      clientY - centerY;


    const scaleDifference =
      this.#scale /
        oldScale -
      1;


    this.#translate.x -=
      (offsetX * scaleDifference) /
      this.#scale;

    this.#translate.y -=
      (offsetY * scaleDifference) /
      this.#scale;


    this.#requestTransformUpdate();
  }


  /* =========================================================
     LIMIT DRAG AREA
  ========================================================= */

  #constrainTranslation() {
    if (!this.#image) return;


    this.#scale = clamp(
      this.#scale,
      MIN_ZOOM,
      MAX_ZOOM
    );


    /*
      Full image mode:
      always centered.
    */
    if (this.#scale <= MIN_ZOOM) {
      this.#scale = MIN_ZOOM;

      this.#translate.x = 0;

      this.#translate.y = 0;

      return;
    }


    const wrapperRect =
      this.getBoundingClientRect();


    const wrapperWidth =
      wrapperRect.width;

    const wrapperHeight =
      wrapperRect.height;


    if (
      !wrapperWidth ||
      !wrapperHeight
    ) {
      return;
    }


    const image = this.#image;


    let naturalWidth =
      image.naturalWidth;

    let naturalHeight =
      image.naturalHeight;


    /*
      Safe fallback.
    */
    if (
      !naturalWidth ||
      !naturalHeight
    ) {
      naturalWidth =
        wrapperWidth;

      naturalHeight =
        wrapperHeight;
    }


    /*
      Calculate real displayed image dimensions
      when object-fit: contain is used.
    */
    const imageRatio =
      naturalWidth /
      naturalHeight;

    const wrapperRatio =
      wrapperWidth /
      wrapperHeight;


    let displayedWidth;

    let displayedHeight;


    if (imageRatio > wrapperRatio) {
      displayedWidth =
        wrapperWidth;

      displayedHeight =
        displayedWidth /
        imageRatio;
    } else {
      displayedHeight =
        wrapperHeight;

      displayedWidth =
        displayedHeight *
        imageRatio;
    }


    const scaledWidth =
      displayedWidth *
      this.#scale;

    const scaledHeight =
      displayedHeight *
      this.#scale;


    const overflowX =
      Math.max(
        0,
        scaledWidth -
          wrapperWidth
      );

    const overflowY =
      Math.max(
        0,
        scaledHeight -
          wrapperHeight
      );


    /*
      Translation happens before scale,
      therefore divide by scale.
    */
    const maximumX =
      overflowX /
      2 /
      this.#scale;

    const maximumY =
      overflowY /
      2 /
      this.#scale;


    this.#translate.x = clamp(
      this.#translate.x,
      -maximumX,
      maximumX
    );


    this.#translate.y = clamp(
      this.#translate.y,
      -maximumY,
      maximumY
    );
  }


  /* =========================================================
     APPLY CSS TRANSFORM
  ========================================================= */

  #applyTransform = () => {
    this.#animationFrame = null;

    this.#constrainTranslation();


    this.style.setProperty(
      '--drag-zoom-scale',
      String(this.#scale)
    );


    this.style.setProperty(
      '--drag-zoom-translate-x',
      `${this.#translate.x}px`
    );


    this.style.setProperty(
      '--drag-zoom-translate-y',
      `${this.#translate.y}px`
    );
  };


  /* =========================================================
     REQUEST FRAME
  ========================================================= */

  #requestTransformUpdate = () => {
    if (
      this.#animationFrame !== null
    ) {
      return;
    }


    this.#animationFrame =
      requestAnimationFrame(
        this.#applyTransform
      );
  };


  /* =========================================================
     CANCEL FRAME
  ========================================================= */

  #cancelAnimationFrame() {
    if (
      this.#animationFrame === null
    ) {
      return;
    }


    cancelAnimationFrame(
      this.#animationFrame
    );


    this.#animationFrame = null;
  }


  /* =========================================================
     RESET
     THIS IS THE IMPORTANT RAW AURA CHANGE
  ========================================================= */

  #resetZoom = () => {
    /*
      Horizon originally starts the mobile image zoomed.
      Raw Aura instead starts from a complete 1x image.
    */

    this.#scale =
      MIN_ZOOM;

    this.#startScale =
      MIN_ZOOM;

    this.#initialDistance =
      0;


    this.#translate = {
      x: 0,
      y: 0,
    };


    this.#startTranslate = {
      x: 0,
      y: 0,
    };


    this.#startPosition = {
      x: 0,
      y: 0,
    };


    this.#isDragging =
      false;

    this.#hasDraggedBeyondThreshold =
      false;

    this.#hasManualZoom =
      false;


    this.#lastTapTime =
      0;

    this.#lastTapPosition =
      null;


    /*
      Immediately force 1x in CSS.
    */
    this.style.setProperty(
      '--drag-zoom-scale',
      '1'
    );


    this.style.setProperty(
      '--drag-zoom-translate-x',
      '0px'
    );


    this.style.setProperty(
      '--drag-zoom-translate-y',
      '0px'
    );


    this.#requestTransformUpdate();
  };


  /* =========================================================
     DESTROY
  ========================================================= */

  destroy() {
    this.#controller.abort();

    this.#resizeObserver.disconnect();

    this.#cancelAnimationFrame();
  }
}


/* =========================================================
   DISTANCE HELPER
========================================================= */

/**
 * @param {Point | Touch} pointA
 * @param {Point | Touch} pointB
 * @returns {number}
 */
function getDistance(
  pointA,
  pointB
) {
  const x1 =
    'clientX' in pointA
      ? pointA.clientX
      : pointA.x;

  const y1 =
    'clientY' in pointA
      ? pointA.clientY
      : pointA.y;


  const x2 =
    'clientX' in pointB
      ? pointB.clientX
      : pointB.x;

  const y2 =
    'clientY' in pointB
      ? pointB.clientY
      : pointB.y;


  const deltaX =
    x1 - x2;

  const deltaY =
    y1 - y2;


  return Math.sqrt(
    deltaX * deltaX +
    deltaY * deltaY
  );
}


/* =========================================================
   REGISTER CUSTOM ELEMENT
========================================================= */

if (
  !customElements.get(
    'drag-zoom-wrapper'
  )
) {
  customElements.define(
    'drag-zoom-wrapper',
    DragZoomWrapper
  );
}